import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// ---- Paylaşılan canlı veri (Firestore okuma tasarrufu) ----
// getDocs her çağrıda koleksiyonun TÜMÜNÜ okur (her belge = 1 okuma).
// Burada her koleksiyon/sorgu için oturum boyunca açık kalan TEK bir onSnapshot dinleyicisi tutulur:
// ilk açılışta bir kez okunur, sonrasında yalnızca değişen belgeler ücretlendirilir.
// Kendi yazdıklarımız ve diğer kullanıcıların değişiklikleri dinleyiciye otomatik yansır.

const entries = new Map(); // key -> { snapshot, items, promise, unsubscribe, subscribers }

const toItems = (snap) => (snap.docs
  ? snap.docs.map(d => ({ id: d.id, ...d.data() }))
  : (snap.exists() ? { id: snap.id, ...snap.data() } : null));

const listen = (key, ref) => {
  const existing = entries.get(key);
  if (existing) return existing;

  const entry = { snapshot: null, items: null, subscribers: new Set(), error: null };
  entry.promise = new Promise((resolve, reject) => {
    entry.unsubscribe = onSnapshot(
      ref,
      (snap) => {
        entry.snapshot = snap;
        entry.items = toItems(snap);
        resolve(snap);
        entry.subscribers.forEach(cb => cb(entry));
      },
      (err) => {
        console.error(`Canlı veri dinlenemedi (${key}):`, err);
        // Bir sonraki istekte yeniden denensin
        entries.delete(key);
        entry.error = err;
        reject(err);
        entry.subscribers.forEach(cb => cb(entry));
      }
    );
  });
  entries.set(key, entry);
  return entry;
};

const current = (entry) => (entry.snapshot ? Promise.resolve(entry.snapshot) : entry.promise);

// getDocs(collection(db, name)) yerine: aynı QuerySnapshot'ı döndürür (.docs, .forEach çalışır)
export const getLiveCollection = (name) => current(listen(name, collection(db, name)));

// getDoc(doc(db, 'settings', 'global')) yerine: getLiveDoc('settings/global')
export const getLiveDoc = (path) => current(listen(path, doc(db, path)));

// Özel sorgular için (anahtar sorguyu tekil tanımlamalı)
export const getLiveQuery = (key, queryRef) => current(listen(key, queryRef));

// onSnapshot(ref, onData, onError) yerine: bileşen kapanınca dinleyici kapanmaz, tekrar açılışta yeniden okunmaz
export const subscribeLive = (key, ref, onData, onError) => {
  const entry = listen(key, ref);
  const cb = (e) => (e.error ? onError?.(e.error) : onData(e.snapshot));
  entry.subscribers.add(cb);
  if (entry.snapshot) onData(entry.snapshot);
  return () => { entry.subscribers.delete(cb); };
};

// Bileşende canlı liste: { items, loading, error } — items: [{ id, ...data }]
export const useLiveData = (key, makeRef, enabled = true) => {
  const [state, setState] = useState(() => {
    const entry = enabled ? entries.get(key) : null;
    return { items: entry?.items ?? null, loading: enabled && !entry?.items, error: null };
  });

  useEffect(() => {
    if (!enabled) {
      setState({ items: null, loading: false, error: null });
      return undefined;
    }
    const entry = listen(key, makeRef());
    const update = (e) => setState({ items: e.items, loading: !e.items && !e.error, error: e.error });
    entry.subscribers.add(update);
    if (entry.items) update(entry);
    return () => { entry.subscribers.delete(update); };
    // makeRef her render'da yeni olabilir; dinleyici anahtara bağlıdır
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
};

export const useLiveCollection = (name, enabled = true) => useLiveData(name, () => collection(db, name), enabled);

// Çıkışta tüm dinleyicileri kapat (başka kullanıcıya veri sızmasın, yetki hatası oluşmasın)
export const stopAllLiveData = () => {
  entries.forEach(entry => entry.unsubscribe?.());
  entries.clear();
};
